import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import PercentageInput from 'components/PercentageInput';
import currencyFormatter from 'utils/currencyFormatter';
import percentFormatter from 'utils/percentFormatter';
import CurrencyInput from 'components/CurrencyInput';
import ModalDelete from 'components/ModalDelete';
import {
  getDetalleTarifa,
  getDetalleTarifas,
  postDetalleTarifa,
  patchDetalleTarifa,
  deleteDetalleTarifa,
} from 'api/estructuraOrganizacional/tarifaDetalle';
import { getPermisos } from 'api/usuarios';
import { useDispatch, useSelector } from 'react-redux';
import { selectPermisos } from 'store/catalogos';
import { checkToken } from 'api/utils';
import { FLOAT_NUMER_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
} from 'utils/constants';

export const rolesPermission = {
  permissionModel: 'detalledetarifaisr',
  catalogsModels: [],
};

/* eslint-disable react/prop-types */
const TarifaISRDetalle = ({
  permission,
  selected,
  disableControls,
  estatus,
}) => {
  const dispatch = useDispatch();
  const permisos = useSelector(selectPermisos);
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDetalleTarifas({ catalogo_tarifa: selected });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_data] = await Promise.all([
        getDetalleTarifas({ catalogo_tarifa: selected }),
        getPermisos(dispatch, permisos),
      ]);
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const tarifa = await getDetalleTarifa(match.id);
    setTimeout(() => {
      if (tarifa) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(tarifa));
          formDatos.setFieldsValue(formatReceived(tarifa));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchDetalleTarifa(key, { ...values, catalogo_tarifa: selected }) : postDetalleTarifa({ ...values, catalogo_tarifa: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteDetalleTarifa(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setTimeout(() => {
      form.setFieldsValue(record);
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Cuota Fija',
      dataIndex: 'cuota_fija',
      key: 'cuota_fija',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: '% Sobre Excedente',
      dataIndex: 'porcentaje_sobre_excedente',
      key: 'porcentaje_sobre_excedente',
      align: 'right',
      render: (val) => percentFormatter(val || 0),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          mobileCols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          allowDoubleClick={estatus === ESTATUS_ACTIVO}
          disableAdd={disableControls}
          disableEdit={disableControls}
          disableDelete={disableControls}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Detalle de Tarifa`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Col xs={24} sm={24} md={24}>
              <CurrencyInput
                allowClear
                name="limite_inferior"
                rules={rules.valor}
                label="Límite Inferior"
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <CurrencyInput
                allowClear
                name="cuota_fija"
                rules={rules.valor}
                label="Cuota Fija"
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <PercentageInput
                allowClear
                name="porcentaje_sobre_excedente"
                rules={rules.valor}
                label="% Sobre Excedente"
                hasFeedback
              />
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Detalle de Tarifa${''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TarifaISRDetalle;
